import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { doctorService } from '@/services/api';
import { toast } from 'sonner';
import {
  Search,
  UserCog,
  Mail,
  Phone,
  Building2,
  Calendar,
  Clock,
  ShieldCheck,
  ChevronRight,
  ChevronDown,
  Stethoscope,
  Plus,
  Pencil,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthorizedButton } from '@/components/common/AuthorizedButton';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import DoctorEditModal from './DoctorEditModal';

import { useAuth } from '@/contexts/AuthContext';
import { formatDoctorName, getDoctorInitials } from '@/lib/utils';

const DoctorListPage = () => {
  const navigate = useNavigate();
  const { buildUrl, availableClinics, activeClinicId } = useClinic();
  const { hasPermission } = useAuth();
  const canViewAllClinics = hasPermission('CLINIC_ALL_VIEW') || hasPermission('PLATFORM_MANAGE');

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Multi-select clinic IDs filter with activeClinicId as default
  const [selectedClinicIds, setSelectedClinicIds] = useState(() => (activeClinicId ? [activeClinicId] : ['all']));
  const [clinicDropdownOpen, setClinicDropdownOpen] = useState(false);

  // Edit doctor modal state
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    loadDoctors();
  }, []);

  const loadDoctors = async () => {
    try {
      setLoading(true);
      const response = await doctorService.getAll();
      setDoctors(response.data || []);
    } catch (error) {
      toast.error('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  };

  const filteredDoctors = doctors.filter(d => {
    const matchesSearch = !search || (
      d.firstName?.toLowerCase().includes(search.toLowerCase()) ||
      d.lastName?.toLowerCase().includes(search.toLowerCase()) ||
      d.name?.toLowerCase().includes(search.toLowerCase()) ||
      d.specialization?.toLowerCase().includes(search.toLowerCase()) ||
      d.registrationNo?.toLowerCase().includes(search.toLowerCase())
    );
    const matchesClinic =
      selectedClinicIds.includes('all') ||
      selectedClinicIds.length === 0 ||
      d.assignedClinics?.some(cId => selectedClinicIds.includes(cId));
    return matchesSearch && matchesClinic;
  });

  return (
    <div className="space-y-6" data-testid="doctor-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Hospital Doctors Directory</h1>
          <p className="text-slate-500 text-sm">
            View physician profiles, clinical specializations, and OPD duty schedules
          </p>
        </div>
        <AuthorizedButton
          permission="STAFF_MANAGE"
          tooltipMessage="Only hospital administrators can onboard new physicians"
          onClick={() => navigate(buildUrl('/doctors/new'))}
          className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs shadow-sm h-10 px-4"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Onboard Physician
        </AuthorizedButton>
      </div>

      {/* Search and Filters */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="relative min-w-[240px] max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="search"
              placeholder="Search by doctor name, specialization, or MCI reg..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 text-xs"
              data-testid="doctor-search"
            />
          </div>

          {canViewAllClinics && (
            <div className="flex items-center gap-2">
              <Popover open={clinicDropdownOpen} onOpenChange={setClinicDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-9 border-slate-300 font-medium flex items-center gap-2 bg-white hover:bg-slate-50 min-w-[170px] justify-between shadow-sm"
                    data-testid="clinic-filter-dropdown"
                  >
                    <div className="flex items-center gap-1.5 truncate">
                      <Building2 className="h-3.5 w-3.5 text-teal-600 shrink-0" />
                      <span className="truncate">
                        {selectedClinicIds.includes('all') || selectedClinicIds.length === 0
                          ? 'All Clinics'
                          : selectedClinicIds.length === 1
                          ? (availableClinics?.find(c => c.id === selectedClinicIds[0])?.name || '1 Clinic Selected')
                          : `${selectedClinicIds.length} Clinics Selected`}
                      </span>
                    </div>
                    {selectedClinicIds.length > 1 && !selectedClinicIds.includes('all') && (
                      <Badge variant="secondary" className="h-4 px-1.5 text-[10px] bg-teal-100 text-teal-800 font-bold ml-1">
                        {selectedClinicIds.length}
                      </Badge>
                    )}
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 p-3 space-y-3">
                  <div className="flex items-center justify-between border-b pb-2">
                    <span className="text-xs font-bold text-slate-800">Filter by Clinic</span>
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setSelectedClinicIds(['all'])}
                        className="text-[11px] h-6 px-1.5 text-teal-700 hover:text-teal-800 hover:bg-teal-50"
                      >
                        All
                      </Button>
                      <span className="text-slate-300">|</span>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setSelectedClinicIds(activeClinicId ? [activeClinicId] : ['all'])}
                        className="text-[11px] h-6 px-1.5 text-slate-600 hover:text-slate-900"
                      >
                        Current
                      </Button>
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1">
                    <label
                      className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={selectedClinicIds.includes('all')}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedClinicIds(['all']);
                          } else {
                            setSelectedClinicIds(activeClinicId ? [activeClinicId] : []);
                          }
                        }}
                      />
                      <span className="text-xs font-semibold text-slate-800">All Clinics</span>
                    </label>

                    <div className="h-px bg-slate-100 my-1" />

                    {availableClinics?.map((c) => {
                      const isChecked = !selectedClinicIds.includes('all') && selectedClinicIds.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-100 cursor-pointer transition-colors"
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => {
                              setSelectedClinicIds((prev) => {
                                const withoutAll = prev.filter(id => id !== 'all');
                                if (withoutAll.includes(c.id)) {
                                  const next = withoutAll.filter(id => id !== c.id);
                                  return next.length === 0 ? ['all'] : next;
                                } else {
                                  return [...withoutAll, c.id];
                                }
                              });
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-slate-800 truncate">{c.name}</p>
                            <p className="text-[10px] text-slate-400">ID: {c.id}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Doctors Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      ) : filteredDoctors.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDoctors.map((doctor) => {
            const resolvedClinicNames = doctor.assignedClinics?.map(cId => availableClinics?.find(c => c.id === cId)?.name || cId).filter(Boolean).join(', ') || doctor.clinics?.map(c => c.name).join(', ') || 'General OPD';

            return (
              <Card
                key={doctor.id}
                className="border-slate-200 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer group"
                onClick={() => navigate(buildUrl(`doctors/${doctor.id}`))}
                data-testid={`doctor-card-${doctor.id}`}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-14 w-14 ring-2 ring-teal-500/20">
                      <AvatarImage src={doctor.avatar} />
                      <AvatarFallback className="bg-teal-100 text-teal-800 font-bold text-base">
                        {getDoctorInitials(doctor)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <h3 className="font-bold text-base text-slate-900 group-hover:text-teal-600 transition-colors truncate">
                          {formatDoctorName(doctor)}
                        </h3>
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <Badge
                            variant="outline"
                            className={doctor.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]' : 'bg-slate-100 text-slate-600 text-[10px]'}
                          >
                            {doctor.status === 'active' ? 'Active Duty' : 'On Leave'}
                          </Badge>
                          {hasPermission('STAFF_MANAGE') && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-slate-400 hover:text-teal-700 hover:bg-teal-50"
                              title="Edit Doctor Info"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingDoctor(doctor);
                                setEditModalOpen(true);
                              }}
                              data-testid={`edit-doctor-btn-${doctor.id}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs font-semibold text-teal-700 mt-0.5">{doctor.specialization}</p>
                      <p className="text-[11px] text-slate-500 truncate">{doctor.qualification}</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-3 rounded-lg text-xs space-y-1.5 text-slate-600">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-3.5 w-3.5 text-teal-600 shrink-0" />
                      <span className="truncate">Reg: {doctor.registrationNo || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="truncate">
                        {resolvedClinicNames}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span>{doctor.dutySchedule || 'Mon-Sat 09:00-17:00'}</span>
                    </div>
                  </div>
                </CardContent>

                <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between text-xs text-teal-700 font-semibold group-hover:text-teal-800">
                  <span>View Profile & Weekly Schedule</span>
                  <ChevronRight className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-12 text-center border-slate-200">
          <Stethoscope className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <h3 className="font-semibold text-slate-800">No Doctors Found</h3>
          <p className="text-xs text-slate-500 mt-1">Try modifying your search or clinic filters.</p>
        </Card>
      )}

      {/* Edit Doctor Modal */}
      <DoctorEditModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingDoctor(null);
        }}
        doctor={editingDoctor}
        onSuccess={loadDoctors}
      />
    </div>
  );
};

export default DoctorListPage;
